import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  ReferenceLine,
  ReferenceDot,
} from "recharts";
import {
  formatCurrencyGbp,
  formatCurrencyGbpCompact,
} from "@shared-core/domain/formatters";
import type { ContributionBurnUp } from "@shared-core/domain/contributionBurnUp";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "../../primitives/chart";

// Monthly burn-up: cumulative booked contribution vs a straight pace line to
// target. Minimal furniture — no y-axis or grid; the hero number carries the
// current value, the target is labelled on the pace line's endpoint, and the
// today tick reads in working days. Forecast-domain pink ramp (booked = the
// actuals pink from LayeredTimelineChart). Built on the house ChartContainer,
// so hover shows the styled tooltip.
const NEUTRAL = "#a3a3a3";
const AXIS = "#737373";

const PINK = "#f472b6"; // pink-400 — shared with the Pipeline card's quote bars

const chartConfig = {
  cumulative: { label: "Booked", color: PINK },
  pace: { label: "Pace", color: NEUTRAL },
} satisfies ChartConfig;

export function ContributionBurnUpChart({
  burnUp,
  targetGbp,
  actualGbp,
}: {
  burnUp: ContributionBurnUp;
  targetGbp: number;
  actualGbp: number;
}) {
  const { points, today, daysInMonth, workingDayOfMonth, workingDaysInMonth } =
    burnUp;

  // No published target → no pace line, no target chip, no target dot. The
  // chart still shows the real booked-contribution burn-up (honest: you see
  // your actual bookings), auto-scaled to the data instead of a fabricated
  // target. See ContributionTargetCard's empty state.
  const hasTarget = targetGbp > 0;

  return (
    <div className="relative w-full">
      {/* Target chip — solid background so it never gets lost over the line */}
      {hasTarget && (
        <div className="absolute top-0 right-1 z-10 rounded-md bg-background px-2 py-0.5 text-sm font-semibold text-foreground">
          Target {formatCurrencyGbpCompact(targetGbp)}
        </div>
      )}
      <ChartContainer config={chartConfig} className="aspect-auto h-28 w-full">
        <ComposedChart
          data={points}
          margin={{ top: 26, right: 14, bottom: 0, left: 14 }}
        >
          <defs>
            <linearGradient id="contribBurnFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={PINK} stopOpacity={0.3} />
              <stop offset="100%" stopColor={PINK} stopOpacity={0.03} />
            </linearGradient>
          </defs>
          {/* With a target, pin the domain to it so the pace line runs
            corner-to-corner and its endpoint IS the target. Without one,
            auto-scale to the real bookings. */}
          <YAxis hide domain={hasTarget ? [0, targetGbp] : [0, "auto"]} />
          {/* Only the "today" marker — the 1…31 endpoints add nothing */}
          <XAxis
            dataKey="day"
            type="number"
            domain={[1, daysInMonth]}
            ticks={[today]}
            tick={{ fontSize: 11, fill: AXIS }}
            tickFormatter={() =>
              `working day ${workingDayOfMonth} of ${workingDaysInMonth}`
            }
            axisLine={false}
            tickLine={false}
          />
          <ChartTooltip
            cursor={{ stroke: "#d4d4d4", strokeDasharray: "3 3" }}
            content={
              <ChartTooltipContent
                labelFormatter={(_, payload) =>
                  `Day ${payload?.[0]?.payload?.day ?? ""}`
                }
                formatter={(value, name, item) => (
                  <div className="flex w-full items-center gap-2">
                    <div
                      className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
                      style={{ background: item?.color }}
                    />
                    <span className="text-muted-foreground">
                      {chartConfig[name as keyof typeof chartConfig]?.label ??
                        name}
                    </span>
                    <span className="text-foreground ml-auto font-mono font-medium tabular-nums">
                      {formatCurrencyGbp(Number(value))}
                    </span>
                  </div>
                )}
              />
            }
          />
          {/* Pace to target — straight reference line */}
          <Line
            type="linear"
            dataKey="pace"
            stroke={NEUTRAL}
            strokeWidth={2}
            strokeDasharray="6 5"
            dot={false}
            isAnimationActive={false}
          />
          {/* Booked to date — continuous filled area */}
          <Area
            type="monotone"
            dataKey="cumulative"
            stroke={PINK}
            strokeWidth={3.5}
            fill="url(#contribBurnFill)"
            connectNulls={false}
            dot={false}
            isAnimationActive={false}
          />
          <ReferenceLine x={today} stroke="#d4d4d4" strokeDasharray="3 3" />
          <ReferenceDot
            x={today}
            y={actualGbp}
            r={5}
            fill={PINK}
            stroke="#ffffff"
            strokeWidth={2}
          />
          {hasTarget && (
            <ReferenceDot
              x={daysInMonth}
              y={targetGbp}
              r={4}
              fill={NEUTRAL}
              stroke="#ffffff"
              strokeWidth={2}
            />
          )}
        </ComposedChart>
      </ChartContainer>
    </div>
  );
}
